'use client';

/**
 * ItemSearchPanel — shared search UI used by QuickAddSheet and the edit-page
 * AddPanel.
 *
 * Owns: category chips, search input, keyboard navigation, results list.
 * Delegates: what happens when a result is selected (onSelect callback).
 *
 * Props:
 *   onSelect(result, providerConf, effectiveCategory) — called when the user
 *     clicks/keyboards a result. The caller decides what to do (stage vs. save).
 *   renderRowSuffix(result) — optional slot for per-row decorations (e.g. the
 *     QuickAddSheet's staged checkmark / loader).
 *   isRowHighlighted(result) — optional override so the caller can suppress the
 *     default highlight (e.g. when a row is already staged).
 *   maxHeight — Tailwind max-h class for the results list (default "max-h-56").
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { CategoryChips, PROVIDER_CONFIG, ProviderValue } from './category-chips';
import { fetchCategories, searchItems, CategoryData } from '@/lib/api';
import type { SearchResult } from '@/lib/api';
import { useDebounce } from '@/hooks/use-debounce';
import { getItemSubtitle } from '@/lib/item-subtitle';
import { useUserPreferences } from '@/components/providers/user-preferences-provider';

export type { ProviderValue };
export { PROVIDER_CONFIG };

// provider value → category slug for subtitle derivation
const PROVIDER_TO_SLUG: Record<string, string> = {
  movie:      'movie',
  tv:         'tv-show',
  book:       'book',
  restaurant: 'restaurant',
  place:      'place',
  podcast:    'podcast',
};

export interface ProviderConf {
  value: ProviderValue;
  label: string;
  apiProvider: string;
  categorySlug: string;
}

export interface ResolvedCategory {
  _id: string;
  slug: string;
  name: string;
}

export interface ItemSearchPanelProps {
  /** Called when user selects a result row. */
  onSelect: (
    result: SearchResult,
    providerConf: ProviderConf | null,
    effectiveCategory: ResolvedCategory | null
  ) => void;
  /** Optional per-row trailing slot (e.g. checkmark, spinner). */
  renderRowSuffix?: (result: SearchResult) => React.ReactNode;
  /**
   * Optional: return true to suppress the default keyboard-highlight style.
   * Useful when a row is "already staged" and you want a different background.
   */
  suppressHighlight?: (result: SearchResult) => boolean;
  /** Tailwind max-h class for the results list. Default: "max-h-56". */
  maxHeight?: string;
  /** Initial provider selection. Default: "movie". */
  initialProvider?: ProviderValue;
  /** Called whenever the active provider chip changes. */
  onProviderChange?: (provider: ProviderValue) => void;
}

export function ItemSearchPanel({
  onSelect,
  renderRowSuffix,
  suppressHighlight,
  maxHeight = 'max-h-56',
  initialProvider = 'movie',
  onProviderChange,
}: ItemSearchPanelProps) {
  const { countryCode } = useUserPreferences();

  const [selectedProvider, setSelectedProvider] = useState<ProviderValue>(initialProvider);

  function handleProviderChange(p: ProviderValue) {
    setSelectedProvider(p);
    onProviderChange?.(p);
  }
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const debouncedSearch = useDebounce(searchQuery, 400);

  const providerConf = (PROVIDER_CONFIG.find((p) => p.value === selectedProvider) as ProviderConf | undefined) ?? null;

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
  });

  const categories = categoriesData?.categories ?? [];
  const categoryBySlug = Object.fromEntries(
    categories.map((c: CategoryData) => [c.slug, c])
  );

  const effectiveCategory = providerConf
    ? (categoryBySlug[providerConf.categorySlug] as ResolvedCategory | undefined) ?? null
    : null;

  const { data: searchData, isFetching: isSearching } = useQuery({
    queryKey: ['items', 'search', providerConf?.apiProvider, providerConf?.value, debouncedSearch, countryCode],
    queryFn: () =>
      searchItems(debouncedSearch, providerConf!.apiProvider, {
        mediaType:
          selectedProvider === 'tv' ? 'tv'
          : selectedProvider === 'movie' ? 'movie'
          : undefined,
        countryCode,
        categorySlug: providerConf?.categorySlug,
      }),
    enabled: !!providerConf && debouncedSearch.length >= 2 && !!effectiveCategory,
  });

  // Reset highlight whenever results change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchData]);

  const results = searchData?.results ?? [];
  const isPoster = selectedProvider === 'movie' || selectedProvider === 'tv';

  return (
    <div className="space-y-3">
      <CategoryChips selected={selectedProvider} onChange={handleProviderChange} />

      <div className="space-y-2">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder={`Search ${providerConf?.label ?? 'items'}…`}
              value={searchQuery}
              className="pl-9 text-base"
              autoComplete="off"
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setHighlightedIndex(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && results.length > 0) {
                  e.preventDefault();
                  onSelect(results[highlightedIndex] ?? results[0], providerConf, effectiveCategory);
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlightedIndex((i) => Math.min(i + 1, results.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlightedIndex((i) => Math.max(i - 1, 0));
                }
              }}
            />
          </div>

          {/* Loading skeletons */}
          {isSearching && (
            <ul className="divide-y border rounded-xl overflow-hidden" aria-busy="true">
              {Array.from({ length: 3 }).map((_, i) => (
                <li key={i} className="flex items-center gap-3 px-3 py-2.5">
                  <Skeleton className={`rounded shrink-0 ${isPoster ? 'h-[42px] w-7' : 'h-9 w-9'}`} />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3 w-3/4" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Results */}
          {!isSearching && results.length > 0 && (
            <ul className={`divide-y border rounded-xl overflow-hidden overflow-y-auto ${maxHeight}`}>
              {results.map((result, idx) => {
                const isHighlighted = idx === highlightedIndex && !suppressHighlight?.(result);
                const subtitle = getItemSubtitle(PROVIDER_TO_SLUG[selectedProvider], result.properties ?? []);
                return (
                  <li
                    key={result.externalId}
                    role="button"
                    tabIndex={0}
                    aria-label={`Add ${result.title}`}
                    onClick={() => onSelect(result, providerConf, effectiveCategory)}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') onSelect(result, providerConf, effectiveCategory);
                    }}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors select-none ${
                      isHighlighted
                        ? 'bg-primary/10 border-l-2 border-l-primary'
                        : 'hover:bg-muted/40 active:bg-muted/70'
                    }`}
                  >
                    {result.thumbnail ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={result.thumbnail}
                        alt=""
                        width={isPoster ? 28 : 36}
                        height={isPoster ? 42 : 36}
                        className={`rounded object-cover shrink-0 ${isPoster ? 'h-[42px] w-7' : 'h-9 w-9'}`}
                      />
                    ) : (
                      <div className={`rounded bg-muted shrink-0 ${isPoster ? 'h-[42px] w-7' : 'h-9 w-9'}`} />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{result.title}</p>
                      {subtitle && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{subtitle}</p>
                      )}
                    </div>
                    {renderRowSuffix?.(result)}
                  </li>
                );
              })}
            </ul>
          )}

          {/* Hints */}
          {!isSearching && debouncedSearch.length >= 2 && results.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-1">No results found.</p>
          )}
          {!isSearching && debouncedSearch.length < 2 && (
            <p className="text-xs text-muted-foreground text-center">
              Type at least 2 characters to search.
            </p>
          )}
      </div>
    </div>
  );
}
